import { useEffect } from 'react';

import { useSearchParams } from 'react-router-dom';

import { CURRENT_PAYMENT_APP_VERSION } from '../config/constants';
import { SKRILL_SESSION_ID_STORAGE_KEY } from '../features/payments/constants';
import { logToNewRelic } from '../services/debug';
import { resolveFlag, useFlag } from '../services/launchDarkly';
import type { EnumPaymentMethods } from '../stores/payredeem/types';
import { EnumOpenDialog } from '../stores/ui/types';
import { useStores } from '../stores/useStores';
import { devError } from '../utils/log';
import { LAST_STARTED_TRANSACTION } from '../utils/payment-app.utils';

import { useStartGame } from './useStartGame';

const methods = new Set(['skrill', 'bank', 'card']);

export const useProcessPaymentRedirect = () => {
  const [search, setSearchParams] = useSearchParams();
  const { uiStore, appStore, payRedeemStore } = useStores();
  const { handleStartGame } = useStartGame();

  const paymentsVersion = useFlag<number>('payments_app_version', CURRENT_PAYMENT_APP_VERSION);

  // v4 and v5
  useEffect(() => {
    if (paymentsVersion !== 4 && paymentsVersion !== 5) {
      return;
    }
    const method = search.get('method');
    const type = search.get('type');

    if (type === 'purchase' && methods.has(<string>method) && appStore.storePackagesFetched) {
      // blocks feature manager from showing the dialog
      payRedeemStore.setAfterPurchaseFlowInProgress(true);
      const status = search.get('status');
      switch (status) {
        case 'failed':
          if (method !== 'card') {
            uiStore.setOpenDialog({
              dialog: EnumOpenDialog.isOpenTransactionFailedDialog,
              options: { type: 'purchase' },
            });
          }
          break;
        case 'complete': {
          const transactionId = search.get('transactionId');
          const shouldTrackSkrillSession = resolveFlag<boolean>('track_skrill_payment_session', false);

          logToNewRelic('process redirect - status complete', 'process redirect - status complete', 'info', {
            method,
            type,
            shouldTrackSkrillSession,
            transactionId,
          });

          if (method === 'skrill' && shouldTrackSkrillSession) {
            const expectedSessionId = sessionStorage.getItem(SKRILL_SESSION_ID_STORAGE_KEY);
            const actualSessionId = search.get(SKRILL_SESSION_ID_STORAGE_KEY);
            sessionStorage.removeItem(SKRILL_SESSION_ID_STORAGE_KEY);

            logToNewRelic(
              'process redirect - status complete, method skrill',
              'process redirect - status complete, method skrill',
              'info',
              {
                method,
                type,
                shouldTrackSkrillSession,
                transactionId,
                actualSessionId,
                expectedSessionId,
              },
            );

            if (!actualSessionId || !expectedSessionId || actualSessionId !== expectedSessionId) {
              setSearchParams({});

              return;
            }
          }

          const packageId = search.get('packageId');
          const gameId = search.get('gameId');
          const isFtd = search.get('isFtd') === 'true';

          if (packageId) {
            appStore.setSelectedPackageId(Number(packageId));
          }
          if (gameId) {
            handleStartGame(Number(gameId), localStorage.getItem('GAME_LAUNCH_TRIGGER') || 'unknown');
          }
          if (method !== 'card' && transactionId) {
            const packageData = sessionStorage.getItem('purchaseActiveEvent');
            if (packageData) {
              try {
                appStore.setCurrentOrderPackage(JSON.parse(packageData));
              } catch (e) {
                devError('Error parsing purchaseActiveEvent', e);
              }
            }
            const storePackage = appStore.storePackagesRegistry.get(Number(packageId));
            payRedeemStore.transactionsEventsRegistry.set(transactionId, {
              transactionId,
              amount: storePackage?.price || 0,
              paymentMethod: method as EnumPaymentMethods,
              selectedPackage: storePackage,
              isHandled: false,
              isFirstDeposit: isFtd,
            });
            payRedeemStore.setInProcessTransactionId(transactionId);
            payRedeemStore.handleSuccessPurchase();
          }
          break;
        }
      }
      setSearchParams('');
    }
  }, [
    appStore,
    appStore.storePackagesFetched,
    handleStartGame,
    payRedeemStore,
    search,
    setSearchParams,
    uiStore,
    paymentsVersion,
  ]);

  // v2
  useEffect(() => {
    if (paymentsVersion === 4 || paymentsVersion === 5) {
      return;
    }
    const method = search.get('method');
    const type = search.get('type');
    if (type === 'purchase' && methods.has(method!) && appStore.storePackagesFetched) {
      const status = search.get('status');
      const packageId = search.get('packageId');
      const gameId = search.get('gameId');

      setSearchParams('');
      switch (status) {
        case 'failed':
          if (method !== 'card') {
            uiStore.setOpenDialog({
              dialog: EnumOpenDialog.isOpenTransactionFailedDialog,
              options: { type: 'purchase' },
            });
          } else {
            window.parent.postMessage({ action: 'close', status: 'error' }, '*');
          }
          break;
        case 'complete': {
          let lastTransaction;

          try {
            lastTransaction = JSON.parse(sessionStorage.getItem(LAST_STARTED_TRANSACTION) || '');
            sessionStorage.removeItem(LAST_STARTED_TRANSACTION);
          } catch (e) {
            // silent catch
          }

          logToNewRelic(
            'pre sendSuccessPurchaseEvent',
            {
              message: 'in useProcessPaymentRedirect',
              data: {
                packageId,
                gameId,
                method,
                lastTransaction,
              },
            },
            'info',
          );

          if (packageId) {
            appStore.setSelectedPackageId(Number(packageId));
          }
          if (gameId) {
            handleStartGame(Number(gameId), localStorage.getItem('GAME_LAUNCH_TRIGGER') || 'unknown');
          }
          if (method !== 'card') {
            if (lastTransaction && lastTransaction.transactionId) {
              payRedeemStore.setInProcessTransactionId(lastTransaction.transactionId);
              payRedeemStore.transactionsEventsRegistry.set(lastTransaction.transactionId, {
                ...lastTransaction,
                paymentMethod: method,
              });
              payRedeemStore.setAfterPurchaseFlowInProgress(true);
              payRedeemStore.handleSuccessPurchase();
            }
          } else {
            window.parent.postMessage({ action: 'close', status }, '*');
          }
          break;
        }
      }
    }
  }, [
    appStore,
    appStore.storePackagesFetched,
    handleStartGame,
    payRedeemStore,
    paymentsVersion,
    search,
    setSearchParams,
    uiStore,
  ]);
};
